import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  useActivateTalentMutation,
  useDeleteTalentMutation,
  useDisableTalentMutation,
  useGetTalentsQuery,
  useRestoreTalentMutation,
  useAiTalentSearchMutation,
} from "../../services/talent.service";
import Stack from "@mui/material/Stack";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Talent } from "../../types/users";
import { useNavigate } from "react-router-dom";
import { formatMoney, isManage360 } from "../../utils/helper";
import {
  Avatar,
  Chip,
  IconButton,
  Link,
  Rating,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  Paper,
  Fade,
  alpha,
  CircularProgress,
} from "@mui/material";
import LocationOn from "@mui/icons-material/LocationOn";
import { FilterAlt, FilterAltOutlined, Download as DownloadIcon, AutoAwesome, Close } from "@mui/icons-material";
import ModalTalentFilter from "./ModalTalentFilter";
import { useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import dayjs from "dayjs";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";


const TalentsPage = () => {
  useTitle("Talents");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const _isManage360 = React.useMemo(() => isManage360(), []);

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Talent> | null;
  }>({
    open: false,
    row: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const debounceQuery = useDebounce(query, 500);
  const [showFilter, setShowFilter] = React.useState(false);

  // AI Search states
  const [aiSearchMode, setAiSearchMode] = React.useState(false);
  const [aiQuery, setAiQuery] = React.useState("");
  const [aiResults, setAiResults] = React.useState<Talent[]>([]);
  const [aiMeta, setAiMeta] = React.useState<any>(null);
  const [aiPage, setAiPage] = React.useState(1);
  const [doAiSearch, { isLoading: aiSearching }] = useAiTalentSearchMutation();

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  // Trigger AI search when page changes in AI mode
  React.useEffect(() => {
    if (aiSearchMode && aiQuery.trim() && aiPage > 1) {
      handleAiSearch();
    }
  }, [aiPage]); // Only trigger on page change, not on initial mount

  const searchFilter = useAppSelector(
    (state: RootState) => state.ui.talentFilter
  );

  const { data, isLoading, isFetching } = useGetTalentsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    ...searchFilter,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteTalentMutation();
  const [doActivate, { isLoading: activing }] = useActivateTalentMutation();
  const [doDisable, { isLoading: inactiving }] = useDisableTalentMutation();
  const [doRestore, { isLoading: restoring }] = useRestoreTalentMutation();

  const handleDeleteRow = (row: GridRowModel<Talent>) => {
    setDeleteModal({ open: true, row });
  };
  const handleEditRow = (row: GridRowModel<Talent>) => {
    navigate(`/talents/${row.id}/edit`);
  };
  const handleRestoreRow = async (row: GridRowModel<Talent>) => {
    try {
      await doRestore(row.id).unwrap();
      toast.success("Data restored");
    } catch (err) { }
  };

  const rows: GridRowsProp | undefined = React.useMemo(
    () => aiSearchMode ? aiResults : data?.data,
    [aiSearchMode, aiResults, data]
  );

  const effectiveRowCount = React.useMemo(
    () => aiSearchMode ? aiMeta?.total : rowCount,
    [aiSearchMode, aiMeta, rowCount]
  );

  const effectivePage = React.useMemo(
    () => aiSearchMode ? aiPage - 1 : page,
    [aiSearchMode, aiPage, page]
  );

  const handlePageChange = React.useCallback((newPage: number) => {
    if (aiSearchMode) {
      setAiPage(newPage + 1);
    } else {
      setPage(newPage);
    }
  }, [aiSearchMode]);

  const columns: GridColDef[] = React.useMemo(
    () =>
      [
        {
          field: "talent_name",
          headerName: "Talent",
          minWidth: isMobile ? 200 : 300,
          flex: 1,
          sortingOrder: ["desc", "asc", null],
          filterable: true,
          renderCell: (params: GridRenderCellParams<undefined, Talent>) => (
            <Stack direction={"row"} alignItems="center">
              <Avatar
                alt={`${params.row.first_name}`}
                src={`${params.row?.photo?.[0]?.original_url}`}
                sx={{ mr: 1 }}
              />
              <Link
                onClick={() => navigate(`/talents/${params.row.id}`)}
                component="button"
                variant="body2"
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    color: "#E8944A",
                    textDecoration: "underline",
                  },
                }}
              >
                {params.row.first_name} {params.row.last_name}
              </Link>
              <Rating
                value={params.row.total_rating}
                readOnly
                size="small"
                sx={{ ml: 2 }}
              />
            </Stack>
          ),
          disableExport: true,
        },
        {
          field: "name",
          headerName: "Talent Name",
          hide: true,
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${params.row.full_name}`,
        },
        {
          field: "total_rating",
          headerName: "Rating",
          hide: true,
        },
        {
          field: "phone_number",
          headerName: "Phone",
          hide: true,
        },
        {
          field: "email",
          headerName: "Email",
          headerAlign: "center",
          minWidth: isMobile ? 150 : 200,
          width: isMobile ? 150 : 200,
          flex: isMobile ? 0 : undefined,
          sortable: true,
          filterable: true,
          align: "center",
          hide: isMobile,
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${params.row.email}`,
          renderCell: (params: GridRenderCellParams<undefined, Talent>) => {
            return (
              <Link
                component="button"
                variant="body2"
                onClick={() => window.open(`mailto:${params.value}`, "_blank")}
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    color: "#E8944A",
                    textDecoration: "underline",
                  },
                }}
              >
                {params.value}
              </Link>
            );
          },
        },
        {
          field: "balance_decrypt",
          headerName: "Balance",
          headerAlign: "center",
          //minWidth: 120,
          sortable: false,
          filterable: false,
          align: "center",
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${formatMoney(params.row.balance_decrypt)}`,
        },
        {
          field: "physical_address_1",
          headerName: "physical_address_1",
          hide: true,
        },
        {
          field: "zip",
          headerName: "Zip",
          hide: true,
        },
        {
          field: "city",
          headerName: "City",
          headerAlign: "center",
          minWidth: isMobile ? 80 : 120,
          width: isMobile ? 80 : 120,
          flex: isMobile ? 0 : undefined,
          sortable: true,
          filterable: false,
          align: "center",
          hide: isMobile,
        },
        {
          field: "state",
          headerName: "State",
          headerAlign: "center",
          minWidth: isMobile ? 80 : 120,
          width: isMobile ? 80 : 120,
          flex: isMobile ? 0 : undefined,
          sortable: true,
          filterable: false,
          align: "center",
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${params.row.state?.toUpperCase() || ""}`,
          hide: isMobile,
        },
        {
          field: "survey_answer",
          headerName: "Social Media",
          headerAlign: "center",
          minWidth: isMobile ? 100 : 120,
          width: isMobile ? 100 : 120,
          flex: isMobile ? 0 : undefined,
          sortable: true,
          filterable: true,
          align: "center",
          hide: isMobile,
        },
        {
          field: "status",
          headerName: "Status",
          headerAlign: "center",
          minWidth: isMobile ? 80 : 120,
          width: isMobile ? 80 : 120,
          flex: isMobile ? 0 : undefined,
          sortable: true,
          filterable: true,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Talent>) => {
            if (!!params.row?.deleted_at)
              return <Typography variant="body2">Deleted</Typography>;
            return (
              <Chip
                label={params.row.status.toUpperCase()}
                size="small"
                color={params.row.status == "active" ? "success" : undefined}
                sx={{ fontSize: 12 }}
              />
            );
          },
        },
        {
          field: "control",
          headerName: "Toggle",
          headerAlign: "center",
          //minWidth: 10,
          sortable: true,
          filterable: false,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Talent>) => {
            if (!!params.row.deleted_at) {
              return (
                <Button
                  variant="outlined"
                  size="small"
                  color="info"
                  onClick={() => handleRestoreRow(params.row)}
                >
                  Restore
                </Button>
              );
            } else if (params.row.status === "pending") {
              //return null;
              return (
                <Button
                  variant="contained"
                  size="small"
                  color="info"
                  onClick={() => handleUpdateStatusRow(params.row, "active")}
                >
                  Approve
                </Button>
              );
            } else if (params.row.status === "active") {
              return (
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleUpdateStatusRow(params.row, "in-active")}
                >
                  Disable
                </Button>
              );
            } else if (params.row.status === "in-active") {
              return (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => handleUpdateStatusRow(params.row, "active")}
                >
                  Enable
                </Button>
              );
            } else {
              return null;
            }
          },
          disableExport: true,
        },
        {
          field: "rate",
          headerName: "Talent Rate",
          hide: true,
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${params.row.min_pay || "?"} - ${params.row.max_pay || "?"}`,
        },
        {
          field: "commute_radius",
          headerName: "Commute Radius (miles)",
          hide: true,
        },
        {
          field: "created_at",
          headerName: "Date Created",
          sortingOrder: ["asc", "desc", null],
          headerAlign: "center",
          sortable: true,
          filterable: true,
          minWidth: isMobile ? 100 : 120,
          width: isMobile ? 100 : 120,
          flex: isMobile ? 0 : undefined,
          align: "center",
          hide: isMobile,
          valueGetter: (params: GridValueGetterParams<undefined, Talent>) =>
            `${dayjs(params.row?.created_at).format("MMM DD, YYYY")}`,
        },
        {
          field: "control2",
          headerName: "",
          headerAlign: "center",
          sortable: false,
          filterable: false,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Talent>) => {
            if (!!params.row.deleted_at) return null;
            return (
              <Stack direction={"row"}>
                <IconButton onClick={() => handleEditRow(params.row)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleDeleteRow(params.row)}>
                  <DeleteOutline fontSize="small" color="error" />
                </IconButton>
              </Stack>
            );
          },
          disableExport: true,
        },
      ].filter((it) => {
        if (_isManage360) {
          return !["rate", "balance_decrypt"].includes(it.field);
        } else return true;
      }) as GridColDef[],
    [_isManage360, isMobile]
  );

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const handleUpdateStatusRow = async (
    row: GridRowModel<Talent>,
    newStatus: string
  ) => {
    try {
      if (newStatus === "active") {
        await doActivate(row.id).unwrap();
      }
      if (newStatus === "in-active") {
        await doDisable(row.id).unwrap();
      }
      toast.success("Successfully updated");
    } catch (err) { }
  };

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  const filterOn = React.useMemo(() => {
    return (
      (Array.isArray(searchFilter.skills) && searchFilter.skills.length > 0) ||
      !!searchFilter.state ||
      !!searchFilter.city ||
      !!searchFilter.country
    );
  }, [searchFilter]);

  // AI Search handlers
  const handleAiSearch = React.useCallback(async () => {
    if (!aiQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    try {
      const result = await doAiSearch({
        query: aiQuery.trim(),
        page: aiPage,
        per_page: pageSize,
      }).unwrap();

      setAiResults(result.data || []);
      setAiMeta(result.meta || null);
      setAiSearchMode(true);

      if (result.meta?.total === 0) {
        toast.info("No talents found");
      } else {
        toast.success(`Found ${result.meta?.total || 0} talent(s)`);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to search talents");
      setAiResults([]);
      setAiMeta(null);
    }
  }, [aiQuery, aiPage, pageSize, doAiSearch]);

  const handleClearAiSearch = React.useCallback(() => {
    setAiQuery("");
    setAiResults([]);
    setAiMeta(null);
    setAiSearchMode(false);
    setAiPage(1);
  }, []);

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns (not hidden)
    const visibleColumns = columns.filter((col) => !col.hide && col.field);

    // Create CSV header
    const headers = visibleColumns.map((col) => col.headerName || col.field).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns.map((col) => {
        let value: string = "";

        // Use valueGetter if available
        if (col.valueGetter) {
          const params = { row } as GridValueGetterParams<undefined, Talent>;
          const getterValue = col.valueGetter(params);
          value = getterValue != null && getterValue !== undefined
            ? String(getterValue)
            : "";
        } else {
          // Try to get value from row
          const fieldParts = col.field?.split(".") || [];
          const rowValue = fieldParts.reduce((obj: any, part: string) => obj?.[part], row);
          value = rowValue != null && rowValue !== undefined
            ? String(rowValue)
            : "";
        }

        // Escape commas and quotes
        value = value.replace(/"/g, '""');
        if (value.includes(",") || value.includes('"') || value.includes("\n")) {
          value = `"${value}"`;
        }
        return value;
      }).join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Talents_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  return (
    <>
      <CardBase
        title={
          aiSearchMode && aiMeta?.ai_extracted
            ? "Talents - AI Search Results"
            : "Talents"
        }
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {!aiSearchMode ? (
              <>
                <StyledTextField
                  size="small"
                  label="Enter to search.."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  sx={{
                    minWidth: { xs: "100%", sm: 250 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                />
                {!!query && (
                  <Button
                    variant="text"
                    onClick={() => setQuery("")}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { xs: "100%", sm: "auto" },
                      color: "#F4A259",
                      "&:hover": {
                        backgroundColor: "rgba(244, 162, 89, 0.08)",
                      },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </>
            ) : (
              <>
                <Stack spacing={1} sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}>
                  <Box sx={{ position: "relative" }}>
                    <StyledTextField
                      size="small"
                      placeholder='Try: "DevOps Houston", "React 3 years"...'
                      value={aiQuery}
                      onChange={(e) => setAiQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAiSearch();
                        }
                      }}
                      disabled={aiSearching}
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mr: 1,
                              color: "#F4A259",
                            }}
                          >
                            <AutoAwesome
                              sx={{
                                fontSize: 20,
                                animation: aiSearching
                                  ? "pulse 2s ease-in-out infinite"
                                  : "none",
                                "@keyframes pulse": {
                                  "0%, 100%": {
                                    opacity: 1,
                                    transform: "scale(1)",
                                  },
                                  "50%": {
                                    opacity: 0.7,
                                    transform: "scale(1.1)",
                                  },
                                },
                              }}
                            />
                          </Box>
                        ),
                        endAdornment: aiSearching ? (
                          <CircularProgress size={16} sx={{ color: "#F4A259" }} />
                        ) : null,
                      }}
                      sx={{
                        minWidth: { xs: "100%", sm: 350 },
                        width: { xs: "100%", sm: "auto" },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: alpha("#F4A259", 0.05),
                          border: `2px solid ${alpha("#F4A259", 0.2)}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: alpha("#F4A259", 0.08),
                            borderColor: alpha("#F4A259", 0.3),
                          },
                          "&.Mui-focused": {
                            backgroundColor: alpha("#F4A259", 0.1),
                            borderColor: "#F4A259",
                            boxShadow: `0 0 0 3px ${alpha("#F4A259", 0.1)}`,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: alpha("#F4A259", 0.03),
                            borderColor: alpha("#F4A259", 0.1),
                          },
                        },
                      }}
                    />
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  onClick={handleAiSearch}
                  disabled={aiSearching || !aiQuery.trim()}
                  startIcon={
                    aiSearching ? (
                      <CircularProgress size={16} sx={{ color: "white" }} />
                    ) : undefined
                  }
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: 120 },
                    backgroundColor: "#F4A259",
                    color: "#ffffff",
                    fontWeight: 600,
                    boxShadow: `0 2px 8px ${alpha("#F4A259", 0.3)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#E8944A",
                      boxShadow: `0 4px 12px ${alpha("#F4A259", 0.4)}`,
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: alpha("#F4A259", 0.5),
                      boxShadow: "none",
                    },
                  }}
                >
                  {aiSearching ? "Searching..." : "Search"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Close />}
                  onClick={handleClearAiSearch}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "auto" },
                    borderColor: alpha("#F4A259", 0.5),
                    color: "#F4A259",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: alpha("#F4A259", 0.08),
                      borderColor: "#F4A259",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Exit AI
                </Button>
              </>
            )}
            {!aiSearchMode && (
              <Button
                variant={filterOn ? "contained" : "outlined"}
                startIcon={filterOn ? <FilterAlt /> : <FilterAltOutlined />}
                onClick={() => setShowFilter(true)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "auto" },
                  ...(filterOn ? {
                    backgroundColor: "#F4A259",
                    color: "#ffffff",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#E8944A",
                    },
                  } : {
                    borderColor: "#F4A259",
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                      borderColor: "#F4A259",
                    },
                  }),
                }}
              >
                Filter {filterOn ? "ON" : ""}
              </Button>
            )}
            {!aiSearchMode && (
              <Button
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={() => setAiSearchMode(true)}
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "auto" },
                  borderColor: "#F4A259",
                  color: "#F4A259",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${alpha("#F4A259", 0.05)} 0%, ${alpha("#F4A259", 0.02)} 100%)`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: alpha("#F4A259", 0.1),
                    borderColor: "#F4A259",
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 12px ${alpha("#F4A259", 0.2)}`,
                  },
                }}
              >
                AI Search
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<LocationOn />}
              onClick={() => navigate(`/talents/map`)}
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "auto" },
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
              }}
            >
              {isMobile ? "Map" : "Show Map"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!rows || rows.length === 0}
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "auto" },
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
                "&:disabled": {
                  borderColor: "rgba(244, 162, 89, 0.5)",
                  color: "rgba(244, 162, 89, 0.5)",
                },
              }}
            >
              {isMobile ? "Export" : "Export CSV"}
            </Button>
          </Stack>
        }
      >
        {/* AI Search Empty State - Before Search */}
        {aiSearchMode && !aiMeta && !aiSearching && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${alpha("#F4A259", 0.15)} 0%, ${alpha("#F4A259", 0.08)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <AutoAwesome sx={{ fontSize: 40, color: "#F4A259" }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#F4A259",
                mb: 1,
                textAlign: "center",
              }}
            >
              AI Talent Search
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 3,
                textAlign: "center",
                maxWidth: 500,
              }}
            >
              Use natural language to find the perfect talent for your project
            </Typography>
            <Stack spacing={1} sx={{ width: "100%", maxWidth: 400 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 1,
                }}
              >
                Try these examples:
              </Typography>
              {[
                "DevOps engineer in Houston",
                "React developer with 3 years experience",
                "Senior Python developer",
                "Full stack engineer near Austin",
              ].map((example, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  onClick={() => {
                    setAiQuery(example);
                    setTimeout(() => handleAiSearch(), 100);
                  }}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    border: `1px solid ${alpha("#F4A259", 0.2)}`,
                    borderRadius: 1,
                    backgroundColor: alpha("#F4A259", 0.03),
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha("#F4A259", 0.08),
                      borderColor: "#F4A259",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {example}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* AI Understanding Display */}
        {aiSearchMode && aiMeta?.ai_extracted && aiMeta?.search_criteria && (
          <Fade in={true} timeout={500}>
            <Paper
              elevation={0}
              sx={{
                m: { xs: 1.5, sm: 2 },
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha("#F4A259", 0.12)} 0%, ${alpha("#F4A259", 0.06)} 100%)`,
                border: `2px solid ${alpha("#F4A259", 0.2)}`,
                backdropFilter: "blur(10px)",
                boxShadow: `0 4px 16px ${alpha("#F4A259", 0.15)}`,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, #F4A259 0%, #E8944A 100%)`,
                      boxShadow: `0 2px 8px ${alpha("#F4A259", 0.4)}`,
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 18, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#F4A259",
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    AI Understood Your Search
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ gap: 1, ml: { xs: 0, sm: 5 } }}
                >
                  {aiMeta.search_criteria.role && (
                    <Chip
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Role: {aiMeta.search_criteria.role}
                        </Typography>
                      }
                      size="small"
                      sx={{
                        backgroundColor: "#F4A259",
                        color: "white",
                        fontWeight: 600,
                        height: 28,
                        boxShadow: `0 2px 4px ${alpha("#F4A259", 0.3)}`,
                      }}
                    />
                  )}
                  {aiMeta.search_criteria.skills &&
                    aiMeta.search_criteria.skills.length > 0 && (
                      <Chip
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Skills: {aiMeta.search_criteria.skills.join(", ")}
                          </Typography>
                        }
                        size="small"
                        sx={{
                          backgroundColor: "#F4A259",
                          color: "white",
                          fontWeight: 600,
                          height: 28,
                          boxShadow: `0 2px 4px ${alpha("#F4A259", 0.3)}`,
                        }}
                      />
                    )}
                  {aiMeta.search_criteria.location?.city && (
                    <Chip
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Location:{" "}
                          {aiMeta.search_criteria.location.city}
                          {aiMeta.search_criteria.location.state
                            ? `, ${aiMeta.search_criteria.location.state}`
                            : ""}
                        </Typography>
                      }
                      size="small"
                      sx={{
                        backgroundColor: "#F4A259",
                        color: "white",
                        fontWeight: 600,
                        height: 28,
                        boxShadow: `0 2px 4px ${alpha("#F4A259", 0.3)}`,
                      }}
                    />
                  )}
                  {aiMeta.search_criteria.years_experience && (
                    <Chip
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Experience: {aiMeta.search_criteria.years_experience} years
                        </Typography>
                      }
                      size="small"
                      sx={{
                        backgroundColor: "#F4A259",
                        color: "white",
                        fontWeight: 600,
                        height: 28,
                        boxShadow: `0 2px 4px ${alpha("#F4A259", 0.3)}`,
                      }}
                    />
                  )}
                </Stack>
                {aiMeta.total > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      ml: { xs: 0, sm: 5 },
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    Found <strong style={{ color: "#F4A259" }}>{aiMeta.total}</strong>{" "}
                    talent{aiMeta.total !== 1 ? "s" : ""} matching your criteria
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Fade>
        )}
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={effectiveRowCount}
          page={effectivePage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode={aiSearchMode ? "client" : "server"}
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={(isLoading || isFetching) && !aiSearchMode || aiSearching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={
            aiSearchMode && !aiMeta && !aiSearching
              ? 0 // Hide grid when showing empty state
              : aiSearchMode && aiMeta?.ai_extracted
                ? isMobile
                  ? "calc(85vh - 280px)"
                  : "calc(85vh - 220px)"
                : isMobile
                  ? "calc(85vh - 140px)"
                  : "calc(85vh - 80px)"
          }
          sx={{
            ...(aiSearchMode && !aiMeta && !aiSearching && { display: "none" }),
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                paddingLeft: { xs: 1, sm: 3 },
                paddingRight: { xs: 1, sm: 3 },
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: { xs: 1, sm: 3 },
              paddingRight: { xs: 1, sm: 3 },
              py: { xs: 1, sm: 2 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "rgba(244, 162, 89, 0.04)",
              },
            },
            "& .MuiDataGrid-main": {
              padding: { xs: 1, sm: 2 },
            },
            "& .MuiDataGrid-footerContainer": {
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 0 },
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete User#${deleteModal.row?.id} '${deleteModal.row?.full_name}' ?`}
      />
      <ModalTalentFilter
        open={showFilter}
        onClose={() => setShowFilter(false)}
      />
    </>
  );
};
export default TalentsPage;
